import { Activity } from '@la/types';
import { getScheduleServiceXRequestId } from 'lib/auth/auth';
import baseApi from './baseApi';
import {
  ProgramPermissions,
  ProgramPermissionsResponse,
  ScoreUpdateObj,
  UserRsvp,
  UserRsvpResponse,
  RosterResponse,
  UserSummaryResponse,
} from './types/singleActivity';

export const singleActivityApi = baseApi
  .enhanceEndpoints({ addTagTypes: ['SingleActivity'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getSingleActivity: builder.query<
        Activity,
        { activityId: number; siteId: string | null; subdomain: string }
      >({
        query: ({ siteId, activityId, subdomain }) => {
          return {
            url: `/api/member-portal/${subdomain}/singleActivity`,
            method: 'GET',
            params: {
              activityId,
              siteId,
            },
            headers: {
              'X-Request-ID': getScheduleServiceXRequestId(),
            },
          };
        },
        providesTags: ['SingleActivity'],
      }),
      getUserSummary: builder.query<
        UserSummaryResponse,
        {
          activityId: number;
          siteId: string | null;
          subdomain: string;
          isGame?: boolean;
        }
      >({
        query: ({ siteId, activityId, subdomain, isGame }) => {
          return {
            url: `/api/member-portal/${subdomain}/singleActivity/userSummary`,
            method: 'GET',
            params: {
              activityId,
              siteId,
              isGame,
            },
            headers: {
              'X-Request-ID': getScheduleServiceXRequestId(),
            },
          };
        },
      }),
      getProgramPermissions: builder.query<
        ProgramPermissions,
        { subdomain: string; programId?: number }
      >({
        query: ({ programId, subdomain }) => {
          return {
            url: `/api/member-portal/${subdomain}/singleActivity/permissions`,
            method: 'GET',
            params: {
              programId,
            },
          };
        },
        transformResponse: (response: ProgramPermissionsResponse) => {
          return response.permissions[0];
        },
      }),
      getProgramSettings: builder.query<
        any,
        { subdomain: string; programId?: number }
      >({
        query: ({ programId, subdomain }) => {
          return {
            url: `/api/member-portal/${subdomain}/singleActivity/settings`,
            method: 'GET',
            params: {
              programId,
            },
          };
        },
      }),
      getSingleActivityRoster: builder.query<
        RosterResponse,
        {
          subdomain: string;
          siteId: string;
          teamId: string;
        }
      >({
        query: ({ subdomain, siteId, teamId }) => ({
          url: `/api/member-portal/${subdomain}/singleActivity/roster`,
          params: { siteId, teamId },
        }),
      }),
      getRsvpResponse: builder.query<
        UserRsvp,
        { activityId?: number; subdomain: string }
      >({
        query: ({ activityId, subdomain }) => {
          return {
            url: `/api/member-portal/${subdomain}/singleActivity/rsvp`,
            method: 'GET',
            params: {
              activityId,
            },
            headers: {
              'X-Request-ID': getScheduleServiceXRequestId(),
            },
          };
        },
      }),
      postRsvpResponse: builder.mutation<
        UserRsvp,
        { activityId?: number; response: UserRsvpResponse; subdomain: string }
      >({
        query: ({ activityId, response, subdomain }) => {
          return {
            url: `/api/member-portal/${subdomain}/singleActivity/rsvp`,
            method: 'POST',
            body: {
              activityId,
              response,
            },
            headers: {
              'X-Request-ID': getScheduleServiceXRequestId(),
            },
          };
        },
      }),
      updateRsvpResponse: builder.mutation<
        UserRsvp,
        { activityId?: number; response: UserRsvpResponse; subdomain: string }
      >({
        query: ({ activityId, response, subdomain }) => {
          return {
            url: `/api/member-portal/${subdomain}/singleActivity/rsvp`,
            method: 'PATCH',
            body: {
              activityId,
              response,
            },
            headers: {
              'X-Request-ID': getScheduleServiceXRequestId(),
            },
          };
        },
      }),
      updateGameScore: builder.mutation<
        any,
        {
          subdomain: string;
          activityId?: string;
          op: string;
          score: ScoreUpdateObj;
          userId: number;
        }
      >({
        query: ({ subdomain, activityId, op, score, userId }) => {
          return {
            url: `/api/member-portal/${subdomain}/singleActivity/score`,
            method: 'PATCH',
            body: {
              activityId,
              op,
              score,
              userId,
            },
            headers: {
              'X-Request-ID': getScheduleServiceXRequestId(),
            },
          };
        },
        invalidatesTags: ['SingleActivity'],
      }),
    }),
  });

export const {
  useGetSingleActivityQuery,
  useGetUserSummaryQuery,
  useGetProgramPermissionsQuery,
  useGetProgramSettingsQuery,
  useGetSingleActivityRosterQuery,
  useGetRsvpResponseQuery,
  useUpdateGameScoreMutation,
  usePostRsvpResponseMutation,
  useUpdateRsvpResponseMutation,
} = singleActivityApi;
